import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import CheckIcon from '@mui/icons-material/Check';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { adminApi } from '../services/api';
import { useThemeMode } from '../contexts/ThemeModeContext';

const Settings = () => {
  const theme = useTheme();
  const { mode, setMode, actualTheme } = useThemeMode();
  const [formData, setFormData] = useState({
    username: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await adminApi.changePassword({
        username: formData.username,
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      });
      setSuccess('Password changed successfully');
      setFormData({
        username: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const themeOptions = [
    { value: 'light', icon: <LightModeIcon />, label: 'Light' },
    { value: 'dark', icon: <DarkModeIcon />, label: 'Dark' },
    { value: 'system', icon: <SettingsBrightnessIcon />, label: `System (${actualTheme === 'dark' ? 'Dark' : 'Light'})` },
  ];

  return (
    <Container maxWidth={false}>
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          background: theme => `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
          borderRadius: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <SettingsRoundedIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Settings
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Customize your app's appearance and manage admin settings
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Theme Settings */}
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              borderColor: theme => alpha(theme.palette.primary.main, 0.2),
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <PaletteRoundedIcon 
                sx={{ 
                  fontSize: 28,
                  color: 'primary.main',
                }} 
              />
              <Typography variant="h6" fontWeight="bold">
                Theme Settings
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {themeOptions.map((option) => (
                <Grid item xs={12} sm={4} key={option.value}>
                  <Card
                    onClick={() => setMode(option.value)}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      position: 'relative',
                      transition: 'all 0.2s ease-in-out',
                      transform: mode === option.value ? 'scale(1.02)' : 'scale(1)',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: theme => `0 8px 24px ${
                          theme.palette.mode === 'dark'
                            ? 'rgba(0, 0, 0, 0.5)'
                            : 'rgba(0, 0, 0, 0.15)'
                        }`,
                      },
                      border: theme => `2px solid ${
                        mode === option.value 
                          ? theme.palette.primary.main
                          : alpha(theme.palette.divider, 0.1)
                      }`,
                      bgcolor: theme => mode === option.value
                        ? alpha(theme.palette.primary.main, 0.1)
                        : 'transparent',
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          bgcolor: theme => mode === option.value
                            ? 'primary.main'
                            : alpha(theme.palette.divider, 0.1),
                          transition: 'all 0.2s',
                          boxShadow: theme => mode === option.value
                            ? `0 0 20px ${alpha(theme.palette.primary.main, 0.4)}`
                            : 'none',
                        }}
                      >
                        {React.cloneElement(option.icon, {
                          sx: {
                            fontSize: 30,
                            color: mode === option.value ? 'white' : 'text.secondary',
                          }
                        })}
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: mode === option.value ? 600 : 400,
                          color: mode === option.value ? 'primary.main' : 'text.primary',
                          textAlign: 'center',
                        }}
                      >
                        {option.label}
                      </Typography>
                    </Box>
                    {mode === option.value && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: 2,
                        }}
                      >
                        <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Password Change Section */}
        {showPasswordSection && (
          <Grid item xs={12} md={6}>
          <Paper
            component="form"
            onSubmit={handleSubmit}
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              borderColor: theme => alpha(theme.palette.primary.main, 0.2),
            }}
          >
            <Stack spacing={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <SecurityRoundedIcon 
                  sx={{ 
                    fontSize: 28,
                    color: 'primary.main',
                  }} 
                />
                <Typography variant="h6" fontWeight="bold">
                  Change Admin Password
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                variant="outlined"
                required
              />

              <TextField
                fullWidth
                label="Current Password"
                name="oldPassword"
                type={showOldPassword ? 'text' : 'password'}
                value={formData.oldPassword}
                onChange={handleChange}
                variant="outlined"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                      >
                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="New Password"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange}
                variant="outlined"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                variant="outlined"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}

              {success && (
                <Typography color="success.main" variant="body2">
                  {success}
                </Typography>
              )}

              <Box display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        )}

        {/* Future Settings Section */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              borderColor: theme => alpha(theme.palette.primary.main, 0.2),
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              More settings coming soon...
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Settings;
